/**
 * Cliente de Payer Authentication 3DS (BanBajío/CyberSource) · Sprint 59 · F1B.
 *
 * Orquesta Setup → Device Data Collection → Enrollment → (Challenge → Validate).
 * NUNCA guarda access tokens ni el token transitorio en localStorage,
 * sessionStorage, query string ni logs: viven sólo en memoria de React o se
 * recuperan del backend (endpoint autenticado del propietario) tras F5.
 * NO autoriza ni captura el pago ni emite boletos (eso es F2).
 */

import { api } from "@/services/api";

/** Origen del backend (para validar postMessage del retorno del challenge). */
export const BACKEND_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_BACKEND_URL || "").origin;
  } catch {
    return "";
  }
})();

/** Hosts oficiales EXPLÍCITOS para URLs externas 3DS (sin regla wildcard). */
export const HOSTS_OFICIALES_3DS = [
  "centinelapistag.cardinalcommerce.com", // Cardinal Cruise · TEST
  "centinelapi.cardinalcommerce.com", // Cardinal Cruise · PROD
  "apitest.cybersource.com", // CyberSource API · TEST
  "api.cybersource.com", // CyberSource API · PROD
];

/** ¿Simulación E2E activa en el frontend? (nunca en producción real). */
export const SIMULACION_3DS =
  String(process.env.REACT_APP_CYBERSOURCE_SIMULATE || "").toLowerCase() === "true";

/** Dimensiones oficiales del challenge por challengeWindowSize. */
export const CHALLENGE_SIZES = {
  "01": [250, 400],
  "02": [390, 400],
  "03": [500, 600],
  "04": [600, 400],
  "05": ["100%", 600],
};

/**
 * Devuelve el origen EXACTO de una URL de DDC/Step-Up si es confiable:
 *  · https obligatorio;
 *  · hostname EXACTO dentro de la lista oficial;
 *  · en simulación E2E se admite además el propio origen del backend.
 * Devuelve null si no es confiable (no se usa ninguna coincidencia por sufijo).
 */
export const origenConfiableExterno = (url) => {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return null;
    const host = u.hostname.toLowerCase();
    if (HOSTS_OFICIALES_3DS.includes(host)) return u.origin;
    if (SIMULACION_3DS && u.origin === BACKEND_ORIGIN) return u.origin;
    return null;
  } catch {
    return null;
  }
};

/** ¿event.origin es exactamente el backend (retorno del challenge)? */
export const esOrigenBackend = (origin) =>
  !!BACKEND_ORIGIN && origin === BACKEND_ORIGIN;

/** Recolecta los campos de navegador PERMITIDOS (no sensibles). */
export const browserInfoActual = () => {
  const nav = typeof window !== "undefined" ? window.navigator : {};
  const scr = typeof window !== "undefined" ? window.screen : {};
  return {
    httpAcceptContent: "application/json",
    httpBrowserColorDepth: String(scr.colorDepth || 24),
    httpBrowserJavaEnabled: false,
    httpBrowserJavaScriptEnabled: true,
    httpBrowserLanguage: nav.language || "es-MX",
    httpBrowserScreenHeight: String(scr.height || 0),
    httpBrowserScreenWidth: String(scr.width || 0),
    httpBrowserTimeDifference: String(new Date().getTimezoneOffset()),
    userAgentBrowserValue: nav.userAgent || "",
  };
};

const _err = (e) => ({
  error: e?.response?.data?.detail || "network_error",
  status: e?.response?.status || 0,
});

export const setup3ds = async (compraId, transientTokenJwt) => {
  try {
    const { data } = await api.post("/pagos/3ds/setup", { compraId, transientTokenJwt });
    return data;
  } catch (e) {
    return _err(e);
  }
};

export const deviceDataComplete = async (sessionId) => {
  try {
    const { data } = await api.post("/pagos/3ds/device-data-complete", { sessionId });
    return data;
  } catch (e) {
    return _err(e);
  }
};

export const enrollment3ds = async (sessionId) => {
  try {
    const { data } = await api.post("/pagos/3ds/enrollment", {
      sessionId,
      browserInfo: browserInfoActual(),
    });
    return data;
  } catch (e) {
    return _err(e);
  }
};

export const challengeStart = async (sessionId) => {
  try {
    const { data } = await api.post("/pagos/3ds/challenge-start", { sessionId });
    return data;
  } catch (e) {
    return _err(e);
  }
};

export const validate3ds = async (sessionId, nonce) => {
  try {
    const { data } = await api.post("/pagos/3ds/validate", { sessionId, nonce });
    return data;
  } catch (e) {
    return _err(e);
  }
};

export const status3ds = async (compraId) => {
  try {
    const { data } = await api.get(`/pagos/3ds/status`, { params: { compraId } });
    return data;
  } catch (e) {
    return _err(e);
  }
};
